import {
  Box,
  FormLabel,
  FormControl,
  HStack,
  VStack,
  Icon,
  Textarea,
  Button,
  Input,
  SimpleGrid,
  Tooltip,
  Text,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";
import { DataScopeProto, ScenarioAnalysis } from "build-ts/analyst";
import { FilterProto } from "build-ts/metadata";
import PresetFilters from "./PresetFilters";
import lodash from "lodash";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { getTooltip } from "utils/getMessages";
import { LuScroll } from "react-icons/lu";

interface ScenarioCreationComponentProps {
  dataScope: DataScopeProto | undefined;
  isCreatingScenario: boolean;
  // For bulk creation of scenarios
  scenario: ScenarioAnalysis | undefined;
  setScenario: React.Dispatch<React.SetStateAction<ScenarioAnalysis | undefined>>;
  bulkPresetFiltersMap: Map<string, FilterProto[]>;
  setBulkPresetFiltersMap: React.Dispatch<React.SetStateAction<Map<string, FilterProto[]>>>;
  // For saving scenario
  handleSaveScenario: () => void;
  // For editing scenario
  isEditingScenario: boolean;
  isTabActive: number;
  // For change tracking
  hasScenarioChanges?: boolean;
  setHasScenarioChanges?: (hasChanges: boolean) => void;
}

const ScenarioCreationComponent: React.FC<ScenarioCreationComponentProps> = ({
  dataScope,
  isCreatingScenario,
  // For bulk creation of scenarios
  scenario,
  setScenario,
  bulkPresetFiltersMap,
  setBulkPresetFiltersMap,
  // For saving scenario
  handleSaveScenario,
  isEditingScenario,
  isTabActive,
  hasScenarioChanges,
  setHasScenarioChanges,
}) => {
  const [issueDescription, setIssueDescription] = useState<string>("");
  const [triggerConditions, setTriggerConditions] = useState<string>("");
  const [investigationProtocol, setInvestigationProtocol] = useState<string>("");
  const [reportingInstructions, setReportingInstructions] = useState<string>("");
  const [analysisInstructions, setAnalysisInstructions] = useState<string>("");

  // Original data state for tracking changes
  const [originalData, setOriginalData] = useState<{
    issueDescription: string;
    triggerConditions: string;
    investigationProtocol: string;
    reportingInstructions: string;
    analysisInstructions: string;
    presetFilters: Map<string, FilterProto[]>;
  }>({
    issueDescription: "",
    triggerConditions: "",
    investigationProtocol: "",
    reportingInstructions: "",
    analysisInstructions: "",
    presetFilters: new Map(),
  });

  // Get current form data
  const getCurrentFormData = () => {
    return {
      issueDescription,
      triggerConditions,
      investigationProtocol,
      reportingInstructions,
      analysisInstructions,
      presetFilters: bulkPresetFiltersMap,
    };
  };

  // Check if form has changes
  useEffect(() => {
    if (!isEditingScenario) {
      // For new scenarios, always show buttons
      setHasScenarioChanges?.(true);
      return;
    }

    const currentFormData = getCurrentFormData();

    // Convert Maps to arrays for comparison
    const currentFiltersArray = Array.from(currentFormData.presetFilters.entries());
    const originalFiltersArray = Array.from(originalData.presetFilters.entries());

    const isChanged =
      currentFormData.issueDescription !== originalData.issueDescription ||
      currentFormData.triggerConditions !== originalData.triggerConditions ||
      currentFormData.investigationProtocol !== originalData.investigationProtocol ||
      currentFormData.reportingInstructions !== originalData.reportingInstructions ||
      currentFormData.analysisInstructions !== originalData.analysisInstructions ||
      !lodash.isEqual(currentFiltersArray, originalFiltersArray);

    setHasScenarioChanges?.(isChanged);
  }, [
    issueDescription,
    triggerConditions,
    investigationProtocol,
    reportingInstructions,
    analysisInstructions,
    bulkPresetFiltersMap,
    isEditingScenario,
  ]);

  // Initialize form fields when editing
  useEffect(() => {
    if (scenario) {
      const issueValue = scenario.issue || "";
      const triggerValue = scenario.trigger || "";
      const investigationValue = scenario.investigation_protocol || "";
      const reportingValue = scenario.reporting_instructions || "";
      const analysisValue = scenario.analysis_instructions || "";

      setIssueDescription(issueValue);
      setTriggerConditions(triggerValue);
      setInvestigationProtocol(investigationValue);
      setReportingInstructions(reportingValue);
      setAnalysisInstructions(analysisValue);

      // Set original data for change tracking (when editing)
      if (isEditingScenario) {
        setOriginalData({
          issueDescription: issueValue,
          triggerConditions: triggerValue,
          investigationProtocol: investigationValue,
          reportingInstructions: reportingValue,
          analysisInstructions: analysisValue,
          presetFilters: new Map(bulkPresetFiltersMap),
        });
      }
    } else {
      // Reset form when creating new
      setIssueDescription("");
      setTriggerConditions("");
      setInvestigationProtocol("");
      setReportingInstructions("");
      setAnalysisInstructions("");

      // Reset original data
      setOriginalData({
        issueDescription: "",
        triggerConditions: "",
        investigationProtocol: "",
        reportingInstructions: "",
        analysisInstructions: "",
        presetFilters: new Map(),
      });
    }
  }, [scenario?.id, isCreatingScenario]); // Only trigger when scenario ID changes

  // Update scenario when form fields change
  useEffect(() => {
    setScenario({
      id: scenario?.id, // Keep existing ID if editing
      disable: scenario?.disable,
      issue: issueDescription,
      trigger: triggerConditions,
      investigation_protocol: investigationProtocol,
      reporting_instructions: reportingInstructions,
      analysis_instructions: analysisInstructions,
      scope_subset: DataScopeProto.create({
        use_default_scope: true,
      }),
      preset_filters: [], // Will be set during save
    });
  }, [
    issueDescription,
    triggerConditions,
    investigationProtocol,
    reportingInstructions,
    analysisInstructions,
  ]);

  // Handle discard changes
  const handleDiscardChanges = () => {
    if (originalData) {
      setIssueDescription(originalData.issueDescription);
      setTriggerConditions(originalData.triggerConditions);
      setInvestigationProtocol(originalData.investigationProtocol);
      setReportingInstructions(originalData.reportingInstructions);
      setAnalysisInstructions(originalData.analysisInstructions);
      setBulkPresetFiltersMap(new Map(originalData.presetFilters));
      setHasScenarioChanges?.(false);
    }
  };

  return (
    <Box
      mx="auto"
      overflowY="auto"
      sx={{
        "&::-webkit-scrollbar": {
          display: "none"
        },
        msOverflowStyle: "none",
        scrollbarWidth: "none"
      }}
    >
      {/* Preset Filters Section */}
      <Box mb={3}>
        <PresetFilters
          dataScope={dataScope}
          bulkPresetFiltersMap={bulkPresetFiltersMap}
          setBulkPresetFiltersMap={setBulkPresetFiltersMap}
          isEditingScenario={isEditingScenario}
          isTabActive={isTabActive}
        />
      </Box>

      {/* Analysis Details Section */}
      <VStack spacing={3} align="stretch" p={2} borderWidth="1px" borderColor="gray.200" borderRadius="md">
        <HStack
          spacing={2}
          p={1}
          borderRadius="md"
          m={1}
          mb={0}
          pb={0}
        >
          <Icon as={LuScroll} boxSize={5} color="fg.default" />
          <Text fontSize="md" fontWeight="700" color="gray.800" mb={0}>
            Analysis Details
          </Text>
          <Tooltip
            label={getTooltip("scenario_creation_component.details.tooltip") || ""}
            placement="top"
            hasArrow
            bg="gray.700"
            color="white"
            fontSize="sm"
            px={3}
            py={2}
            borderRadius="md"
            ml={2}
          >
            <Box as="span" cursor="help">
              <Icon as={IoMdInformationCircleOutline} boxSize={6} color="blue.300" />
            </Box>
          </Tooltip>
        </HStack>

        {/* Issue Description - Single Line Input */}
        <FormControl p={1} m={1} mb={0} pb={0}>
          <FormLabel fontSize="sm" mb={1} fontWeight="700">
            <HStack spacing={2}>
              <Text m={0}>
                Issue
              </Text>
              <Tooltip
                label={getTooltip("scenario_creation_component.details.issue.tooltip") || ""}
                placement="top"
                hasArrow
                bg="gray.700"
                color="white"
                fontSize="sm"
                px={2}
                py={2}
                borderRadius="md"
              >
                <Box as="span" cursor="help" p={0} m={0} mb={0.5}>
                  <Icon as={IoMdInformationCircleOutline} boxSize={4} color="blue.300" />
                </Box>
              </Tooltip>
            </HStack>
          </FormLabel>
          <Input
            size="sm"
            value={issueDescription}
            onChange={(e) => {
              setIssueDescription(e.target.value);
            }}
            placeholder="Describe the issue..."
            _focus={{
              borderColor: "purple.700",
              boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
            }}
            fontWeight="500"
          />
        </FormControl>

        {/* Remaining Fields in 2-Column Grid */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} p={1} m={1}>
          {/* Trigger Conditions */}
          <FormControl>
            <FormLabel fontSize="sm" mb={1} fontWeight="700">
              <HStack spacing={2}>
                <Text m={0}>
                  Trigger Conditions
                </Text>
                <Tooltip
                  label={getTooltip("scenario_creation_component.details.trigger_conditions.tooltip") || ""}
                  placement="top"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  fontSize="sm"
                  px={2}
                  py={2}
                  borderRadius="md"
                >
                  <Box as="span" cursor="help" p={0} m={0} mb={0.5}>
                    <Icon as={IoMdInformationCircleOutline} boxSize={4} color="blue.300" />
                  </Box>
                </Tooltip>
              </HStack>
            </FormLabel>
            <Textarea
              size="sm"
              value={triggerConditions}
              onChange={(e) => {
                setTriggerConditions(e.target.value);
              }}
              placeholder="Define when this scenario should be triggered..."
              rows={6}
              _focus={{
                borderColor: "purple.700",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
              }}
              fontWeight="500"
              resize="none"
            />
          </FormControl>

          {/* Investigation Protocol */}
          <FormControl>
            <FormLabel fontSize="sm" mb={1} fontWeight="700">
              <HStack spacing={2}>
                <Text m={0}>
                  Investigation Protocol
                </Text>
                <Tooltip
                  label={getTooltip("scenario_creation_component.details.investigation_protocol.tooltip") || ""}
                  placement="top"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  fontSize="sm"
                  px={2}
                  py={2}
                  borderRadius="md"
                >
                  <Box as="span" cursor="help" p={0} m={0} mb={0.5}>
                    <Icon as={IoMdInformationCircleOutline} boxSize={4} color="blue.300" />
                  </Box>
                </Tooltip>
              </HStack>
            </FormLabel>
            <Textarea
              size="sm"
              value={investigationProtocol}
              onChange={(e) => {
                setInvestigationProtocol(e.target.value);
              }}
              placeholder="Outline the investigation steps..."
              rows={6}
              _focus={{
                borderColor: "purple.700",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
              }}
              fontWeight="500"
              resize="none"
            />
          </FormControl>

          {/* Reporting Instructions */}
          <FormControl>
            <FormLabel fontSize="sm" mb={1} fontWeight="700">
              <HStack spacing={2}>
                <Text m={0}>
                  Reporting Instructions
                </Text>
                <Tooltip
                  label={getTooltip("scenario_creation_component.details.reporting_instructions.tooltip") || ""}
                  placement="top"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  fontSize="sm"
                  px={2}
                  py={2}
                  borderRadius="md"
                >
                  <Box as="span" cursor="help" p={0} m={0} mb={0.5}>
                    <Icon as={IoMdInformationCircleOutline} boxSize={4} color="blue.300" />
                  </Box>
                </Tooltip>
              </HStack>
            </FormLabel>
            <Textarea
              size="sm"
              value={reportingInstructions}
              onChange={(e) => {
                setReportingInstructions(e.target.value);
              }}
              placeholder="Specify how to report findings..."
              rows={8}
              _focus={{
                borderColor: "purple.700",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
              }}
              fontWeight="500"
              resize="none"
            />
          </FormControl>

          {/* Analysis Instructions */}
          <FormControl>
            <FormLabel fontSize="sm" mb={1} fontWeight="700">
              <HStack spacing={2}>
                <Text m={0}>
                  Analysis Instructions
                </Text>
                <Tooltip
                  label={getTooltip("scenario_creation_component.details.analysis_instructions.tooltip") || ""}
                  placement="top"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  fontSize="sm"
                  px={2}
                  py={2}
                  borderRadius="md"
                >
                  <Box as="span" cursor="help" p={0} m={0} mb={0.5}>
                    <Icon as={IoMdInformationCircleOutline} boxSize={4} color="blue.300" />
                  </Box>
                </Tooltip>
              </HStack>
            </FormLabel>
            <Textarea
              size="sm"
              value={analysisInstructions}
              onChange={(e) => {
                setAnalysisInstructions(e.target.value);
              }}
              placeholder="Provide analysis instructions..."
              rows={8}
              _focus={{
                borderColor: "purple.700",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
              }}
              fontWeight="500"
              resize="none"
            />
          </FormControl>
        </SimpleGrid>
      </VStack>


      {/* Overlay Buttons - Show when creating new or has changes */}
      {hasScenarioChanges && (
        <Box
          position="fixed"
          bottom="20px"
          right="20px"
          bg="white"
          border="1px"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="lg"
          p={3}
          zIndex={1000}
        >
          <HStack spacing={3} justify="space-around">
            <>
              {/* Save/Update Button */}
              <Button
                colorScheme="purple"
                size="md"
                onClick={handleSaveScenario}
                leftIcon={<Icon as={FaCheck} boxSize={3} />}
              >
                {isEditingScenario ? "Update" : "Save"}
              </Button>

              {/* Discard Changes Button - Only show when editing scenario */}
              {isEditingScenario && (
                <Button
                  colorScheme="red"
                  variant="outline"
                  size="md"
                  onClick={handleDiscardChanges}
                  leftIcon={<Icon as={FaTimes} boxSize={3} />}
                >
                  Discard
                </Button>
              )}
            </>
          </HStack>
        </Box>
      )}
    </Box>
  );
};

export default ScenarioCreationComponent;

